import React from "react";
import type { Payment } from "../../../src/pages/loans/LoanDues";

type Props = {
  payments: Payment[];
  isCustomer: boolean;
  onPay: (p: Payment) => void;
  onViewTransaction: (scheduleId?: number | null) => void;
  transactions?: { schedule_id?: number | null }[]; // new prop
};

function Badge({ status }: { status: Payment["status"] }) {
  const base =
    "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium";
  switch (status) {
    case "Paid":
      return (
        <span className={`${base} bg-green-100 text-green-800`}>Paid</span>
      );
    case "Late Paid":
      return (
        <span className={`${base} bg-green-100 text-green-800`}>Late Paid</span>
      );
    case "Upcoming":
      return (
        <span className={`${base} bg-yellow-100 text-yellow-800`}>
          Upcoming
        </span>
      );
    case "Partially Paid":
      return (
        <span className={`${base} bg-orange-100 text-orange-800`}>
          Partially Paid
        </span>
      );
    case "Late":
      return <span className={`${base} bg-red-100 text-red-800`}>Late</span>;
    default:
      return <span className={base}>{status}</span>;
  }
}

function getPaymentTimeliness(
  dueDate?: string | null,
  paymentDate?: string | null,
): { label: string; color: string } {
  if (!dueDate || !paymentDate) {
    return { label: "N/A", color: "bg-gray-100 text-gray-800" };
  }

  try {
    const due = new Date(dueDate);
    const paid = new Date(paymentDate);

    if (isNaN(due.getTime()) || isNaN(paid.getTime())) {
      return { label: "N/A", color: "bg-gray-100 text-gray-800" };
    }

    const diffTime = paid.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return { label: "On Time", color: "bg-green-100 text-green-800" };
    } else if (diffDays <= 2) {
      return {
        label: `${diffDays} Day Late`,
        color: "bg-blue-100 text-blue-800",
      };
    } else {
      return {
        label: `${diffDays} Days Late`,
        color: "bg-orange-100 text-orange-800",
      };
    }
  } catch {
    return { label: "N/A", color: "bg-gray-100 text-gray-800" };
  }
}

export default function DuePaymentsTable({
  payments,
  isCustomer,
  onPay,
  onViewTransaction,
  transactions,
}: Props) {
  return (
    <div className="mt-6">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 ">
          <thead className=" ">
            <tr className="text-xs text-color text-left main-bg-color  ">
              <th className="px-4 py-3 border-r-1 border-white">S.No</th>
              <th className="px-4 py-3 border-r-1 border-white">Due Date</th>
              <th className="px-4 py-3 border-r-1 border-white">Amount Due</th>
              <th className="px-4 py-3 border-r-1 border-white">Status</th>
              <th className="px-4 py-3 border-r-1 border-white">Paid</th>
              <th className="px-4 py-3 border-r-1 border-white">
                Payment Date
              </th>
              <th className="px-4 py-3 border-r-1 border-white">Actions</th>
              <th className="px-4 py-3 border-r-1 border-white">Receipt</th>
              <th className="px-4 py-3">Timeliness</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-300 text-nowrap">
            {payments.map((p, idx) => (
              <tr key={idx} className="text-sm text-gray-700">
                <td className="px-4 py-4 font-medium">{idx + 1}</td>
                <td className="px-4 py-4">{p.dueDate}</td>
                <td className="px-4 py-4 font-semibold">{p.amount}</td>
                <td className="px-4 py-4">
                  <Badge status={p.status} />
                </td>
                <td className="px-4 py-4 font-semibold">{p.totalPaid}</td>
                <td className="px-4 py-4">{p.paymentDate ?? "-"}</td>
                {/* ACTIONS COLUMN */}
                <td className="px-4 py-4">
                  {p.status === "Upcoming" ||
                  p.status === "Partially Paid" ||
                  p.status === "Late" ? (
                    <div className="flex flex-col gap-2">
                      <button
                        className={`px-3 py-1 text-xs rounded ${
                          isCustomer
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-sky-600 text-white cursor-pointer"
                        }`}
                        onClick={() => onPay(p)}
                        disabled={isCustomer}
                      >
                        {p.status === "Partially Paid"
                          ? "Pay Remaining"
                          : "Pay Now"}
                      </button>

                      {(p.status === "Partially Paid" || p.status === "Late") &&
                      p.remainingAmount ? (
                        <span className="text-xs text-gray-500">
                          Remaining:{" "}
                          <span className="font-medium">
                            {p.remainingAmount}
                          </span>
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                {/* NEW RECEIPT COLUMN */}
                <td className="px-4 py-4">
                  {(() => {
                    const txs = transactions ?? [];
                    if (!txs.length) {
                      return <span className="text-gray-400 text-sm">-</span>;
                    }

                    // if payment has a schedule_id, check for matching transactions
                    const schedId = p.raw?.schedule_id;
                    const hasMatching =
                      typeof schedId !== "undefined" && schedId !== null
                        ? txs.some((t) => t.schedule_id === schedId)
                        : false;

                    // check if there are any unlinked transactions (no schedule_id)
                    const hasUnlinked = txs.some(
                      (t) =>
                        t.schedule_id === undefined || t.schedule_id === null,
                    );

                    const showLink =
                      (p.status === "Paid" ||
                        p.status === "Partially Paid" ||
                        p.status === "Late Paid") &&
                      (hasMatching || hasUnlinked);

                    if (showLink) {
                      return (
                        <a
                          onClick={() => onViewTransaction(schedId ?? null)}
                          className="text-sm text-blue-600 hover:underline cursor-pointer"
                        >
                          View Receipt
                        </a>
                      );
                    }

                    return <span className="text-gray-400 text-sm">-</span>;
                  })()}
                </td>
                {/* TIMELINESS COLUMN */}
                <td className="px-4 py-4">
                  {p.status === "Paid" || p.status === "Late Paid" ? (
                    (() => {
                      const timeliness = getPaymentTimeliness(
                        p.raw?.due_date,
                        p.raw?.payment_on,
                      );
                      return (
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${timeliness.color}`}
                        >
                          {timeliness.label}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Statistics */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-xs text-gray-600 font-medium">On-Time Payments</p>
          <p className="text-2xl font-bold text-green-700 mt-1">
            {
              payments.filter(
                (p) =>
                  p.status === "Paid" &&
                  getPaymentTimeliness(p.raw?.due_date, p.raw?.payment_on)
                    .label === "On Time",
              ).length
            }
          </p>
        </div>

        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <p className="text-xs text-gray-600 font-medium">Late Payments</p>
          <p className="text-2xl font-bold text-orange-700 mt-1">
            {
              payments.filter(
                (p) =>
                  p.status === "Late Paid" ||
                  (p.status === "Paid" &&
                    getPaymentTimeliness(
                      p.raw?.due_date,
                      p.raw?.payment_on,
                    ).label.includes("Late")),
              ).length
            }
          </p>
        </div>

        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <p className="text-xs text-gray-600 font-medium">Remaining Due</p>
          <p className="text-2xl font-bold text-red-700 mt-1">
            {
              payments.filter(
                (p) =>
                  p.status === "Upcoming" ||
                  p.status === "Partially Paid" ||
                  p.status === "Late",
              ).length
            }
          </p>
        </div>
      </div>

      <div className="mt-6 text-right text-sm text-gray-500">
        Showing {payments.length} scheduled payments
      </div>
    </div>
  );
}
